// The account-key and recipient-list endpoints, with 404 read as "not yet" rather than
// an error: a fresh install has neither.
import { api, type Device, type Schemas } from "../api/client";
import { ApiError, unwrap } from "../api/queries";
import { bytesToBase64 } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";

export type AccountKeyDto = Schemas["AccountKeyDto"];
export type RecipientListDto = Schemas["RecipientListDto"];
export type SealingKeyDto = Schemas["SealingKeyDto"];

/** Who holds the account key's private half (D59): a device, or the platform. */
export type AccountKeyMode = "self" | "managed";

interface Result<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

async function orUndefined<T>(result: Promise<Result<T>>): Promise<T | undefined> {
  try {
    return await unwrap(result);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return undefined;
    throw e;
  }
}

/** A call whose success has no body (204), or whose body we do not need. */
async function expectOk(result: Promise<Result<unknown>>): Promise<void> {
  let r: Result<unknown>;
  try {
    r = await result;
  } catch (e) {
    throw new ApiError(0, e instanceof Error ? e.message : e);
  }
  if (!r.response.ok) throw new ApiError(r.response.status, r.error);
}

export const fetchAccountKey = (): Promise<AccountKeyDto | undefined> => orUndefined(api.GET("/api/account-key"));

export const fetchRecipientList = (): Promise<RecipientListDto | undefined> =>
  orUndefined(api.GET("/api/recipient-list"));

export const fetchDevices = (): Promise<Device[]> => unwrap(api.GET("/api/devices"));

/** Set once; the same key again is fine, a different one is a 409. Needs the device session. */
export const putAccountKey = (alg: SigAlg, publicKey: Uint8Array): Promise<void> =>
  expectOk(api.PUT("/api/account-key", { body: { alg, public_key: bytesToBase64(publicKey) } }));

/**
 * Hand the account key to the platform (D59): the private half travels sealed to the
 * daemon's sealing key; the platform then signs the device list itself.
 */
export const putManagedAccountKey = (alg: SigAlg, publicKey: Uint8Array, sealedPrivateKey: Uint8Array): Promise<void> =>
  expectOk(
    api.PUT("/api/account-key", {
      body: { alg, public_key: bytesToBase64(publicKey), mode: "managed", sealed_private_key: bytesToBase64(sealedPrivateKey) },
    }),
  );

export const fetchSealingKey = (): Promise<SealingKeyDto> => unwrap(api.GET("/api/sealing-key"));

export const putRecipientList = (body: RecipientListDto): Promise<void> =>
  expectOk(api.PUT("/api/recipient-list", { body }));
